/**
 * Admin-side RBAC — mirrors the structure of `../Authorization.ts` exactly but
 * for the platform-admin surface, NOT the tenant org surface.
 *
 * Why a separate axis:
 * - The tenant authorization model resolves through
 *   `authorizationService.getAccessProfile(userId, organizationId)` and is
 *   org-scoped end-to-end. Admin actions are platform-wide and have no
 *   `organizationId` to scope by (admins act across tenants).
 * - The two identity scopes are disjoint: tenant users live in `users`, admin
 *   users live in `admin_users`. Sharing one role/action enum would couple
 *   admin's grant matrix to tenant invariants it has no reason to care about.
 *
 * A Clerk session can only bootstrap `PENDING_ROLE_ASSIGNMENT`; assigning an
 * active role requires `MANAGE_ADMINS`, which only `SUPER_ADMIN` holds.
 */
export var AdminRole;
(function (AdminRole) {
    /** All admin actions. The seed role. */
    AdminRole["SUPER_ADMIN"] = "SUPER_ADMIN";
    /** Billing operator: subscription sync, refunds, AI credits, force-cancel. */
    AdminRole["BILLING_ADMIN"] = "BILLING_ADMIN";
    /** Read-only across orgs + non-destructive support actions (v1.1: trial reset). */
    AdminRole["SUPPORT_ADMIN"] = "SUPPORT_ADMIN";
    /**
     * Default role for an `admin_users` row auto-created by `adminAuthPlugin`
     * on first admin Clerk sign-in. Has NO grants — every gated route 403s. A
     * SUPER_ADMIN must explicitly assign a real role from the Team settings page
     * can do anything. The frontend treats `role === PENDING_ROLE_ASSIGNMENT`
     * the same as 401/403 (redirect to /forbidden).
     */
    AdminRole["PENDING_ROLE_ASSIGNMENT"] = "PENDING_ROLE_ASSIGNMENT";
})(AdminRole || (AdminRole = {}));
export var AdminAction;
(function (AdminAction) {
    // Cross-tenant organization read/maintenance.
    AdminAction["VIEW_ORGANIZATIONS"] = "VIEW_ORGANIZATIONS";
    AdminAction["MANAGE_ORGANIZATIONS"] = "MANAGE_ORGANIZATIONS";
    // Per-org billing actions exposed by the admin billing surface.
    AdminAction["SYNC_BILLING"] = "SYNC_BILLING";
    AdminAction["REFUND_BILLING"] = "REFUND_BILLING";
    AdminAction["GRANT_AI_CREDITS"] = "GRANT_AI_CREDITS";
    AdminAction["CANCEL_SUBSCRIPTION"] = "CANCEL_SUBSCRIPTION";
    AdminAction["MANAGE_ENTITLEMENTS"] = "MANAGE_ENTITLEMENTS";
    // Platform-wide AI pricing rows (list / append / delete-future) and the
    // model catalog LIST. The pricing catalog is append-only; a NULL/empty
    // pricing key is what broke chat billing. Grants are deliberately unchanged
    // — BILLING_ADMIN keeps exactly the authority it already had.
    AdminAction["MANAGE_PLATFORM_PRICING"] = "MANAGE_PLATFORM_PRICING";
    // AI model catalog lifecycle (create / edit / deactivate) and every binding
    // route. SUPER_ADMIN only: repointing which model serves chat, media
    // analysis or voice is closer to the AI proposal kill switch than to
    // billing work, so it does NOT ride on MANAGE_PLATFORM_PRICING.
    AdminAction["MANAGE_AI_MODELS"] = "MANAGE_AI_MODELS";
    // Partnership-orgs surface: create partners, mint/revoke codes, enroll
    // existing orgs, bulk pause/resume AI. Granted to SUPER_ADMIN only in v1.
    AdminAction["MANAGE_PARTNERSHIPS"] = "MANAGE_PARTNERSHIPS";
    // Platform-wide usage & activity metrics (read-only admin dashboard).
    AdminAction["VIEW_USAGE_METRICS"] = "VIEW_USAGE_METRICS";
    // The support queue: read every tenant's tickets, reply as staff, and move
    // a ticket through its statuses. Non-destructive by construction (a reply
    // is append-only, a status is reversible), so it is the first action that
    // gives SUPPORT_ADMIN something to do beyond reading.
    AdminAction["MANAGE_SUPPORT_TICKETS"] = "MANAGE_SUPPORT_TICKETS";
    // Finished-cattle quotes per UF as the product shows them, plus the manual
    // emergency quote an operator enters when a feed is dead or wrong. The value
    // reaches every organization whose price basis is that state, so it stays
    // SUPER_ADMIN-only (implicitly via ALL_ADMIN_ACTIONS), like the kill switch.
    AdminAction["MANAGE_COMMODITY_PRICES"] = "MANAGE_COMMODITY_PRICES";
    // The assistant-proposal kill switch: enable/disable one proposal Action Type
    // platform-wide, with no deploy. The blast radius is "the AI stops proposing
    // and stops being approvable for this write kind", so it stays SUPER_ADMIN-only
    // (granted implicitly via ALL_ADMIN_ACTIONS) rather than joining a role bundle.
    AdminAction["MANAGE_AI_PROPOSAL_ACTIONS"] = "MANAGE_AI_PROPOSAL_ACTIONS";
    // The scheduled-job run log: every firing of every worker job, with its
    // status, counts and errors. Read-only, but it names internal job types,
    // error messages and per-source ingest detail, so it stays SUPER_ADMIN-only
    // (implicitly via ALL_ADMIN_ACTIONS) rather than joining a role bundle.
    AdminAction["VIEW_SCHEDULED_JOBS"] = "VIEW_SCHEDULED_JOBS";
    // Fire a scheduled worker job by hand: an ECS RunTask with the exact
    // parameters the EventBridge schedule would use. Refused while a task of the
    // same job type is still pending or running, and the worker's advisory lock
    // is the backstop, but it still spends a Fargate task and touches every
    // tenant's data, so it stays SUPER_ADMIN-only (implicitly via
    // ALL_ADMIN_ACTIONS), separate from the read grant above.
    AdminAction["MANAGE_SCHEDULED_JOBS"] = "MANAGE_SCHEDULED_JOBS";
    // Message templates: the operator-authored texts the platform sends to its
    // users, one permission for reading and writing. What a tenant reads under
    // the platform's name is a platform decision, so it stays SUPER_ADMIN-only
    // (implicitly via ALL_ADMIN_ACTIONS) until a sending workflow needs to hand
    // it to a role bundle.
    AdminAction["MANAGE_MESSAGE_TEMPLATES"] = "MANAGE_MESSAGE_TEMPLATES";
    // Lifecycle emails: which message template each of the four automatic
    // account/trial emails uses and whether it is switched on. A binding
    // decides what every new organization's owners receive under the
    // platform's name, so it stays SUPER_ADMIN-only (implicitly via
    // ALL_ADMIN_ACTIONS), like MANAGE_MESSAGE_TEMPLATES.
    AdminAction["MANAGE_LIFECYCLE_EMAILS"] = "MANAGE_LIFECYCLE_EMAILS";
    // The system cultivar catalog: the pasture cultivars every organization
    // picks from when composing a paddock. A row here reaches every tenant's
    // cultivar picker and feeds the growth model behind their rotation, so it
    // stays SUPER_ADMIN-only (implicitly via ALL_ADMIN_ACTIONS), like
    // MANAGE_COMMODITY_PRICES. Distinct from the tenant-side
    // `Action.MANAGE_CULTIVARS`, which only ever governs an organization's own
    // cultivars.
    AdminAction["MANAGE_CULTIVARS"] = "MANAGE_CULTIVARS";
    // Soil interpretation references: the published tables that classify every
    // paddock's soil analysis. An edit reclassifies every paddock that uses the
    // reference, and a delete moves those paddocks to the default, so it stays
    // SUPER_ADMIN-only (implicitly via ALL_ADMIN_ACTIONS), like MANAGE_CULTIVARS.
    AdminAction["MANAGE_SOIL_REFERENCES"] = "MANAGE_SOIL_REFERENCES";
    // Admin-fleet visibility is shared by active roles; role assignment remains
    // SUPER_ADMIN-only through MANAGE_ADMINS.
    AdminAction["VIEW_ADMINS"] = "VIEW_ADMINS";
    // Audit + admin-fleet management (v1.1+).
    AdminAction["VIEW_AUDIT_LOG"] = "VIEW_AUDIT_LOG";
    AdminAction["MANAGE_ADMINS"] = "MANAGE_ADMINS";
})(AdminAction || (AdminAction = {}));
export var AdminScope;
(function (AdminScope) {
    /** Single platform-wide scope. There is no per-org admin scope. */
    AdminScope["PLATFORM"] = "PLATFORM";
})(AdminScope || (AdminScope = {}));
/**
 * Single source of truth for permission checking on the admin surface. Shared
 * between backend (the `adminPermission` macro) and frontend (per-feature UI
 * gating once a non-SUPER_ADMIN ships).
 */
export function hasAdminPermission(profile, action) {
    return profile.grants.some((g) => g.action === action && g.scope === AdminScope.PLATFORM);
}
const ALL_ADMIN_ACTIONS = Object.values(AdminAction);
const platformGrants = (actions) => actions.map((action) => ({ action, scope: AdminScope.PLATFORM }));
export const DEFAULT_ADMIN_ROLE_DEFINITIONS = [
    {
        id: AdminRole.SUPER_ADMIN,
        name: "Super Admin",
        grants: platformGrants(ALL_ADMIN_ACTIONS),
    },
    {
        id: AdminRole.BILLING_ADMIN,
        name: "Billing Admin",
        grants: platformGrants([
            AdminAction.VIEW_ORGANIZATIONS,
            AdminAction.VIEW_ADMINS,
            AdminAction.SYNC_BILLING,
            AdminAction.REFUND_BILLING,
            AdminAction.GRANT_AI_CREDITS,
            AdminAction.CANCEL_SUBSCRIPTION,
            AdminAction.MANAGE_ENTITLEMENTS,
            AdminAction.MANAGE_PLATFORM_PRICING,
            AdminAction.VIEW_USAGE_METRICS,
        ]),
    },
    {
        id: AdminRole.SUPPORT_ADMIN,
        name: "Support Admin",
        grants: platformGrants([
            AdminAction.VIEW_ORGANIZATIONS,
            AdminAction.VIEW_ADMINS,
            AdminAction.VIEW_AUDIT_LOG,
            AdminAction.VIEW_USAGE_METRICS,
            AdminAction.MANAGE_SUPPORT_TICKETS,
        ]),
    },
    {
        id: AdminRole.PENDING_ROLE_ASSIGNMENT,
        name: "Pending role assignment",
        // Deliberately empty: an auto-created admin has no grants until a
        // SUPER_ADMIN promotes them. Every `adminPermission(action)` check
        // returns false, so every gated route 403s.
        grants: [],
    },
];
/**
 * Lookup helper for the `adminPermission` macro. Returns the flattened grant
 * list for a role, or an empty list if the role is unknown (defensive — should
 * be impossible given the pgEnum is the same set).
 */
export function grantsForAdminRole(role) {
    const def = DEFAULT_ADMIN_ROLE_DEFINITIONS.find((r) => r.id === role);
    return def ? [...def.grants] : [];
}
//# sourceMappingURL=AdminAuthorization.js.map